// ProfileCompletionBar
//
// Visual "X% complete" bar for an employee profile. Two rendering modes:
//
//   * variant="card"    (default) — full card with bar, percentage, and a
//     short list of the next 3 missing items. Used on EmployeeDetailPage
//     when the viewer is looking at their own profile, so they have a
//     persistent reminder of what's left.
//
//   * variant="inline"  — compact bar only (no missing-items list). Used
//     inside the ProfileEditModal header so the user watches the bar
//     climb as they fill the form in. (The modal uses a draft form state
//     rather than the employee row, so it passes that draft as `data`.)
//
// The data shape it accepts is intentionally loose — both the saved
// employees row AND the in-flight modal form pass the same field names,
// so this component doesn't care which it gets.

import React from 'react';
import { CheckCircle2, Circle, Sparkles } from 'lucide-react';

// Each item counts equally toward 100%. Tests live on the item so
// composite fields (e.g. emergency contact requires both name + phone)
// only count when fully filled.
const PROFILE_FIELDS = [
  { key: 'profile_photo_url',   label: 'Profile photo' },
  { key: 'phone',               label: 'Phone number' },
  { key: 'bio',                 label: 'Short bio' },
  { key: 'address',             label: 'Home address' },
  { key: 'date_of_birth',       label: 'Date of birth' },
  {
    key:   'emergency_contact',
    label: 'Emergency contact',
    test:  (d) => !!(strOk(d?.emergency_contact_name) && strOk(d?.emergency_contact_phone)),
  },
  {
    key:   'skills',
    label: 'At least one skill',
    test:  (d) => Array.isArray(d?.skills) && d.skills.length > 0,
  },
  { key: 'linkedin_url', label: 'LinkedIn URL' },
  {
    key:   'resume_url',
    label: 'Resume / CV',
    // Either an already-saved path OR a pending upload in the modal form.
    test:  (d) => strOk(d?.resume_url) || !!d?._resumePending,
  },
];

function strOk(v) {
  return typeof v === 'string' && v.trim().length > 0;
}

// Pure helper — exported so the reminder banner / sidebar dot can read
// the same numbers without re-rendering the bar.
export function computeProfileCompletion(data) {
  if (!data) {
    return { percent: 0, filled: 0, total: PROFILE_FIELDS.length, fields: [], missing: [] };
  }
  const fields = PROFILE_FIELDS.map((f) => {
    const filled = f.test ? !!f.test(data) : strOk(data[f.key]);
    return { ...f, filled };
  });
  const filled = fields.filter((f) => f.filled).length;
  const percent = Math.round((filled / fields.length) * 100);
  return { percent, filled, total: fields.length, fields, missing: fields.filter((f) => !f.filled) };
}

// Tone of the bar shifts with progress. Below 50 → amber so it reads as
// "needs attention." 50–99 → indigo for "in progress." 100 → emerald.
function barTone(percent) {
  if (percent >= 100) return { bar: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', ring: 'border-emerald-200 dark:border-emerald-800/60' };
  if (percent >= 50)  return { bar: 'bg-indigo-500',  text: 'text-indigo-600 dark:text-indigo-400',   ring: 'border-indigo-200 dark:border-indigo-800/60' };
  return                     { bar: 'bg-amber-500',   text: 'text-amber-600 dark:text-amber-400',     ring: 'border-amber-200 dark:border-amber-800/60' };
}

export default function ProfileCompletionBar({
  data,
  variant   = 'card',
  onAction,           // when set on the card variant, renders a CTA button
  actionLabel = 'Complete profile',
}) {
  const { percent, filled, total, missing } = computeProfileCompletion(data);
  const tone = barTone(percent);
  const done = percent >= 100;

  if (variant === 'inline') {
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium text-slate-700 dark:text-slate-300">
            Profile {filled} of {total} complete
          </span>
          <span className={`font-semibold ${tone.text}`}>{percent}%</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
          <div
            className={`h-full ${tone.bar} transition-all duration-500 ease-out`}
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    );
  }

  // Card variant — full panel with bar + next-up items + optional CTA.
  return (
    <div className={`rounded-2xl border ${tone.ring} bg-white dark:bg-slate-900/40 p-4 sm:p-5`}>
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${done ? 'bg-emerald-500' : 'bg-indigo-500'} flex items-center justify-center`}>
          {done
            ? <CheckCircle2 size={20} className="text-white" />
            : <Sparkles      size={20} className="text-white" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              {done ? 'Profile complete' : 'Profile completion'}
            </p>
            <p className={`text-sm font-bold ${tone.text}`}>{percent}%</p>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {done
              ? 'Nice — your colleagues can see everything they need.'
              : `${filled} of ${total} sections filled in. Finish for a richer profile.`
            }
          </p>

          {/* Progress bar */}
          <div className="mt-3 h-2 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            <div
              className={`h-full ${tone.bar} transition-all duration-500 ease-out`}
              style={{ width: `${percent}%` }}
            />
          </div>

          {/* Missing items — show up to three so it stays glanceable. */}
          {!done && missing.length > 0 && (
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
              {missing.slice(0, 4).map((m) => (
                <li key={m.key} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                  <Circle size={10} className="text-slate-300 dark:text-slate-600 flex-shrink-0" />
                  {m.label}
                </li>
              ))}
              {missing.length > 4 && (
                <li className="text-xs text-slate-400 italic">
                  + {missing.length - 4} more
                </li>
              )}
            </ul>
          )}

          {!done && onAction && (
            <button
              type="button"
              onClick={onAction}
              className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium transition"
            >
              {actionLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
